import { ExternalLink, Mail, Phone } from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';

// Driver-role content mirrored from iOS HelpSupportView / UserGuideView.
const DRIVER_FAQS = [
  {
    q: 'How do I start accepting orders?',
    a: "Make sure you're logged in and your status is set to 'Available'. New orders will appear in your Orders tab.",
  },
  {
    q: 'How do I get paid?',
    a: 'Payments are made outside the app by bank transfer. Add your bank name, account name, and account number under Profile → Payout Details so the team can pay you. CheF keeps a 10% commission on the delivery fee — you receive the rest plus 100% of customer tips. Check the payout calendar on the Earnings tab to see when payments go out.',
  },
  {
    q: 'How far can I deliver?',
    a: 'Your delivery type sets your range. Walking couriers see orders within 0.5 miles of the restaurant, bicycle couriers within 1 mile, and car/scooter drivers have no range limit. Walking and bicycle couriers get first claim on short deliveries.',
  },
  {
    q: 'What if I have issues during delivery?',
    a: "Contact support immediately through the app. We're here to help resolve any delivery issues.",
  },
];

const DRIVER_GUIDES = [
  {
    title: 'Driver Onboarding',
    content:
      'Welcome to the Chef St. Kitts delivery team! Complete your profile, add your delivery type and payout details, enable location and notifications, then go online to start receiving delivery requests.',
  },
  {
    title: 'Managing Deliveries',
    content:
      'New orders appear in the Work tab. Accept an order, navigate to the restaurant, scan the receipt QR code to confirm pickup, then follow GPS directions to the customer and mark the order delivered.',
  },
  {
    title: 'Earnings & Payments',
    content:
      'You earn 90% of each delivery fee — CheF keeps a 10% commission — plus 100% of customer tips. Payments are made outside the app by bank transfer, so add your bank details under Profile → Payout Details. Check the payout calendar on the Earnings tab for payment dates.',
  },
  {
    title: 'Delivery Types & Range',
    content:
      'Walking couriers are offered deliveries within 0.5 miles of the restaurant, bicycle couriers within 1 mile, and car or scooter drivers have no range limit. Short deliveries are reserved for walkers and cyclists when one is active.',
  },
];

function FaqList() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
      {DRIVER_FAQS.map(f => (
        <details key={f.q} className="group px-4 py-3">
          <summary className="text-sm font-medium text-gray-800 cursor-pointer list-none flex items-center justify-between">
            {f.q}
            <span className="text-gray-300 group-open:rotate-90 transition-transform">›</span>
          </summary>
          <p className="text-sm text-gray-500 mt-2">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

// mode: 'support' (Help & Support) | 'guide' (User Guide & FAQ)
export default function HelpSheet({ open, onClose, mode = 'support' }) {
  const isGuide = mode === 'guide';
  return (
    <Sheet open={open} onClose={onClose} title={isGuide ? 'User Guide & FAQ' : 'Help & Support'}>
      <div className="px-4 pb-6 space-y-5">
        {isGuide && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
              User Guide
            </p>
            <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
              {DRIVER_GUIDES.map(g => (
                <details key={g.title} className="group px-4 py-3">
                  <summary className="text-sm font-medium text-gray-800 cursor-pointer list-none flex items-center justify-between">
                    {g.title}
                    <span className="text-gray-300 group-open:rotate-90 transition-transform">›</span>
                  </summary>
                  <p className="text-sm text-gray-500 mt-2">{g.content}</p>
                </details>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
            FAQ
          </p>
          <FaqList />
        </div>

        {!isGuide && (
          <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
            <a
              href="mailto:browneinc7@gmail.com"
              className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-gray-50"
            >
              <span className="w-8 h-8 rounded-lg bg-orange-50 text-[#FF6B35] flex items-center justify-center">
                <Mail size={17} />
              </span>
              <span className="flex-1 text-sm font-medium text-gray-800">Contact Support</span>
            </a>
            <a
              href="https://browne-ink.web.app"
              target="_blank"
              rel="noreferrer"
              className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-gray-50"
            >
              <span className="w-8 h-8 rounded-lg bg-orange-50 text-[#FF6B35] flex items-center justify-center">
                <ExternalLink size={17} />
              </span>
              <span className="flex-1 text-sm font-medium text-gray-800">Visit Support Center</span>
            </a>
            <a
              href="tel:+18696601052"
              className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-gray-50"
            >
              <span className="w-8 h-8 rounded-lg bg-orange-50 text-[#FF6B35] flex items-center justify-center">
                <Phone size={17} />
              </span>
              <span className="flex-1 text-sm font-medium text-gray-800">Call Support</span>
              <span className="text-sm text-gray-400">+1 (869) 660-1052</span>
            </a>
          </div>
        )}
      </div>
    </Sheet>
  );
}
